import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import ContextMenu from '../ContextMenu';
import { Muted } from '../Controls';
import { setContextMenu } from '../ActiveGame/actions';
import { LOG } from '../ActiveGame/contextMenuTypes';
import { selectActiveContextMenu, selectActiveGame } from '../ActiveGame/selectors';
import { selectCardsById } from '../Cards/selectors';
import { getLog } from './actions';
import { selectLogEntries } from './selectors';
import { describeEntry } from './describeEntry';
import { InlineLogArea, Line, LogButton, LogHeading, Move, MoveTime, Turn, TurnPlayer, TurnTitle } from './styled';
import { timeWhen } from '../dates';
import LineCards from './LineCards';
import LogText from './LogText';

/** Opens the game's activity log. */
export const OpenLog = ({ userId }) => {
  const dispatch = useDispatch();
  return <LogButton onClick={() => dispatch(setContextMenu(LOG, userId))}>Log</LogButton>;
};

OpenLog.propTypes = {
  userId: PropTypes.number.isRequired,
};

/**
 * Everything that's happened in the game, newest first: the newest turn at the top, and within a turn the newest move,
 * each with the time it was made; a move's own sentences stay in order, cause then effect. In words (describeEntry). It loads while it's `showing`, and again whenever the game changes.
 */
export const LogTurns = ({ userId, showing }) => {
  const dispatch = useDispatch();
  const game = useSelector(selectActiveGame);
  const gameId = game.get('id');
  const entries = useSelector((state) => selectLogEntries(state, { gameId }));
  const cardsById = useSelector(selectCardsById);

  useEffect(() => {
    if (showing) {
      dispatch(getLog(gameId));
    }
  }, [showing, game, gameId, dispatch]);

  const turns = useMemo(() => {
    if (!entries) {
      return [];
    }
    const context = {
      me: userId,
      cardsById,
    };
    const byTurn = new Map();
    entries.forEach((entry) => {
      const lines = describeEntry(entry, context);
      if (lines.length) {
        byTurn.set(entry.turn, [{ id: entry.id, at: entry.created_at, player: entry.user_id, lines }, ...(byTurn.get(entry.turn) || [])]);
      }
    });
    return [...byTurn.entries()].reverse();
  }, [entries, userId, cardsById]);

  return (
    <>
      {!entries && <Muted>Loading…</Muted>}
      {entries && !turns.length && <Muted>Nothing has happened yet.</Muted>}
      {turns.map(([turn, moves]) => (
        <Turn key={turn}>
          <TurnTitle>
            <span>{turn ? `Turn ${turn}` : 'Setup'}</span>
            {/* A turn is one player's; its first move is theirs (setup is both players'). */}
            {turn > 0 && <TurnPlayer>{moves[moves.length - 1].player === userId ? 'You' : 'Opponent'}</TurnPlayer>}
          </TurnTitle>
          {moves.map(move => (
            <Move key={move.id}>
              {move.at && <MoveTime><time dateTime={move.at}>{timeWhen(move.at)}</time></MoveTime>}
              {move.lines.map((line, i) => (
                <Line key={i}>
                  {line.cards.length > 0 && <LineCards cardIds={line.cards} cardsById={cardsById} />}
                  <LogText text={line.text} />
                </Line>
              ))}
            </Move>
          ))}
        </Turn>
      ))}
    </>
  );
};

LogTurns.propTypes = {
  userId: PropTypes.number.isRequired,
  showing: PropTypes.bool.isRequired,
};

/** The log as a sheet, opened with the Log button (on a phone; a wide screen shows it in the panel: InlineLog). */
const GameLog = ({ userId }) => {
  const open = useSelector(selectActiveContextMenu) === LOG;

  return (
    <ContextMenu menu={LOG} userId={userId} title="Log">
      <LogTurns userId={userId} showing={open} />
    </ContextMenu>
  );
};

GameLog.propTypes = {
  userId: PropTypes.number.isRequired,
};

/** The log always showing, under your hand on a wide screen. */
export const InlineLog = ({ userId }) => (
  <InlineLogArea aria-label="Log">
    <LogHeading>Log</LogHeading>
    <LogTurns userId={userId} showing />
  </InlineLogArea>
);

InlineLog.propTypes = {
  userId: PropTypes.number.isRequired,
};

export default GameLog;
